import React, { useMemo } from 'react'
import { Tree, Radio, Tooltip } from 'antd'
import PropTypes from 'prop-types'
import {
  REGGORA_PANEL_ID,
  REGGORA_NETWORK_NAME,
  LENDER_PANEL_KEY_PREFIX,
} from '../constants/panelSourceConstants'

/**
 * Creates a tree node title with a radio button on the left
 * @param {Object} branch - Branch object: { branch_id, name, identifier }
 * @param {string|null} branchId - The branch_id of this node
 * @param {string|null} selectedBranchId - Currently selected branch_id
 * @param {Function} onRadioChange - Callback when radio is clicked
 * @param {Object} panelistCounts - Object mapping branch_id to panelist count
 * @param {boolean} isDisabled - Whether this branch is disabled
 * @returns {ReactNode} Title element with radio button
 */
const createTreeNodeTitle = (branch, branchId, selectedBranchId, onRadioChange, panelistCounts, isDisabled) => {
  // Handle selection: match by value exactly
  // Important: REGGORA_PANEL_ID is a string, null is for Lender Panel, and other branch_ids are strings
  const isSelected = selectedBranchId === branchId

  // Get panelist count for this branch (0 if not found in panelistCounts)
  const panelistCount = branchId && branchId !== REGGORA_PANEL_ID
    ? (panelistCounts[branchId] || 0)
    : null

  // Single handler to prevent double-firing
  const handleSelection = (e) => {
    e.stopPropagation()
    if (!isSelected && !isDisabled) {
      onRadioChange(branchId, branch)
    }
  }

  // Handle clicks on the label area (outside the radio button)
  const handleLabelClick = (e) => {
    // Only handle if the click is not on the radio button itself
    if (e.target.tagName !== 'INPUT' && !isDisabled) {
      e.stopPropagation()
      if (!isSelected) {
        onRadioChange(branchId, branch)
      }
    }
  }

  const titleContent = (
    <div
      className={`branch-tree-node-title ${isDisabled ? 'branch-tree-node-disabled' : ''}`}
      onClick={handleLabelClick}
      style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
    >
      <Radio
        checked={isSelected}
        onChange={handleSelection}
        disabled={isDisabled}
      />
      <span className="branch-tree-node-label">
        {branch.name}
        {branch.is_strict && (
          <span className="branch-tree-node-strict">
            (Strict)
          </span>
        )}
      </span>
      {panelistCount !== null && (
        <span className="branch-tree-node-count">
          ({panelistCount})
        </span>
      )}
    </div>
  )

  // Wrap in tooltip if disabled
  if (isDisabled) {
    return (
      <Tooltip
        title="This branch does not have any eligible panelists for this order"
        overlayInnerStyle={{ textAlign: 'center' }}
      >
        {titleContent}
      </Tooltip>
    )
  }

  return titleContent
}

/**
 * Generates a unique key for a branch node
 * @param {string|null|undefined} branchId - The branch_id
 * @param {number} index - The index in the branches array
 * @returns {string} A unique key for the node
 */
const generateNodeKey = (branchId, index) => {
  return branchId !== null && branchId !== undefined
    ? String(branchId)
    : `${LENDER_PANEL_KEY_PREFIX}${index}`
}

/**
 * Transforms a flat array of branches (in reverse hierarchical order) into a tree structure
 * Array format: [child, parent, grandparent, ...] where first item is child, second is parent, etc.
 * Example: [childBranch, parentBranch, grandparentBranch] becomes:
 *   grandparentBranch
 *     └── parentBranch
 *         └── childBranch
 * @param {Array} branches - Array of branch objects: [{ branch_id, name, identifier }, ...]
 * @param {string|null} selectedBranchId - Currently selected branch_id or REGGORA_PANEL_ID
 * @param {Function} onRadioChange - Callback when radio is clicked
 * @param {boolean} is_amc_lender - Whether the user is an AMC lender
 * @param {Object} panelistCounts - Object mapping branch_id to panelist count
 * @returns {Array} Tree data structure for Ant Design Tree
 */
const transformBranchesToTree = (branches, selectedBranchId, onRadioChange, is_amc_lender, panelistCounts) => {
  const treeNodes = []

  // Add "Reggora Network" as the first option if not AMC lender
  // Reggora Network is never disabled
  if (!is_amc_lender) {
    const reggoraNetworkNode = {
      title: createTreeNodeTitle(
        { name: REGGORA_NETWORK_NAME, identifier: null },
        REGGORA_PANEL_ID,
        selectedBranchId,
        onRadioChange,
        panelistCounts,
        false, // Never disabled
      ),
      key: REGGORA_PANEL_ID,
      branch_id: REGGORA_PANEL_ID,
      branch: { name: REGGORA_NETWORK_NAME, identifier: null },
      children: [],
      disabled: false,
    }
    treeNodes.push(reggoraNetworkNode)
  }

  // If no branches, return just Reggora Network (if applicable)
  if (!branches || !Array.isArray(branches) || branches.length === 0) {
    return treeNodes
  }

  // The array is in reverse hierarchical order: [child, parent, grandparent, ...]
  // We need to build a tree from root (last item) to leaf (first item)
  // Create a map to track nodes by their key for quick lookup
  const nodeMap = new Map()
  let rootNode = null

  // Process branches from the end (root) to the beginning (child)
  // This ensures parent nodes are created before their children
  for (let i = branches.length - 1; i >= 0; i--) {
    const branch = branches[i]
    const branchId = branch.branch_id
    const key = generateNodeKey(branchId, i)

    // Skip if we've already processed this node
    if (nodeMap.has(key)) {
      continue
    }

    // Determine if this branch should be disabled
    // Never disable Reggora Network (already handled above) or Lender Panel (branch_id is null)
    // Disable branches with 0 panelists
    const isDisabled = branchId !== null &&
                       branchId !== REGGORA_PANEL_ID &&
                       branchId !== undefined &&
                       (panelistCounts[branchId] || 0) === 0

    // Create tree node with radio button in title
    const treeNode = {
      title: createTreeNodeTitle(branch, branchId, selectedBranchId, onRadioChange, panelistCounts, isDisabled),
      key,
      branch_id: branchId,
      branch,
      children: [],
      disabled: isDisabled,
    }

    // Store in map
    nodeMap.set(key, treeNode)

    // If this is the last item (root/grandparent), it's the root node
    if (i === branches.length - 1) {
      rootNode = treeNode
    } else {
      // This node is a child of the next item in the array
      // Find the parent node (which should already be in the map since we process backwards)
      const parentBranch = branches[i + 1]
      const parentId = parentBranch.branch_id
      const parentKey = generateNodeKey(parentId, i + 1)

      const parentNode = nodeMap.get(parentKey)
      if (parentNode) {
        // Add this node as a child of the parent
        parentNode.children.push(treeNode)
      }
    }
  }

  // Add the root node (branch hierarchy) to the tree nodes
  if (rootNode) {
    treeNodes.push(rootNode)
  }

  return treeNodes
}

/**
 * Generic helper function to find a node in the tree by a predicate function
 * @param {Array} nodes - Array of tree nodes to search
 * @param {Function} predicate - Function that returns true when the node matches
 * @param {Function} returnValue - Function that returns what to return when found
 * @returns {*} The return value from returnValue function, or null if not found
 */
const findNodeInTree = (nodes, predicate, returnValue) => {
  for (const node of nodes) {
    if (predicate(node)) {
      return returnValue(node)
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, predicate, returnValue)
      if (found !== null) {
        return found
      }
    }
  }
  return null
}

/**
 * Component to render a branch hierarchy tree using Ant Design Tree
 * @param {Array} branches - Array of branch objects in reverse hierarchical order: [child, parent, grandparent, ...]
 * @param {string|null} selectedBranchId - Currently selected branch_id or REGGORA_PANEL_ID
 * @param {Function} onSelect - Callback when a branch is selected: (branchId, branch) => void
 * @param {boolean} is_amc_lender - Whether the user is an AMC lender
 * @param {Object} panelistCounts - Object mapping branch_id to panelist count
 */
const BranchHierarchyTree = ({ branches, selectedBranchId, onSelect, is_amc_lender, panelistCounts = {} }) => {
  // Transform branches array into tree structure
  // This is expensive computation (loops through branches, builds tree), so memoize it
  const treeData = useMemo(() => {
    return transformBranchesToTree(branches, selectedBranchId, onSelect, is_amc_lender, panelistCounts)
  }, [branches, selectedBranchId, onSelect, is_amc_lender, panelistCounts])

  // Convert selectedBranchId to selectedKeys array for Ant Design Tree
  // Simple computation, but depends on treeData which is memoized, so memoize to avoid recalculation
  const selectedKeys = useMemo(() => {
    if (selectedBranchId === REGGORA_PANEL_ID) {
      return [REGGORA_PANEL_ID]
    }
    if (selectedBranchId === null || selectedBranchId === undefined) {
      // Find the "Lender Panel" node (branch_id is null)
      const lenderPanelNode = treeData.find((node) => node.branch_id === null)
      return lenderPanelNode ? [lenderPanelNode.key] : []
    }
    // Find the node with matching branch_id (for actual branch selections)
    const key = findNodeInTree(
      treeData,
      (node) => node.branch_id === selectedBranchId,
      (node) => node.key,
    )
    return key ? [key] : []
  }, [selectedBranchId, treeData])

  // Handle tree node selection (for keyboard navigation and clicking tree node area)
  const handleSelect = (selectedKeys) => {
    if (selectedKeys.length === 0) {
      return
    }

    const selectedKey = selectedKeys[0]
    const selectedNode = findNodeInTree(
      treeData,
      (node) => node.key === selectedKey,
      (node) => node,
    )

    // Prevent selection of disabled nodes
    if (selectedNode && !selectedNode.disabled && onSelect) {
      onSelect(selectedNode.branch_id, selectedNode.branch)
    }
  }

  // Always show the tree (even if no branches, we might have Reggora Network)
  return (
    <div className="branch-hierarchy-tree">
      <Tree
        treeData={treeData}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        showLine={false}
        blockNode={true}
        defaultExpandAll={true}
      />
    </div>
  )
}

BranchHierarchyTree.propTypes = {
  branches: PropTypes.arrayOf(
    PropTypes.shape({
      branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
      name: PropTypes.string.isRequired,
      identifier: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
      is_strict: PropTypes.bool,
    }),
  ),
  selectedBranchId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  onSelect: PropTypes.func.isRequired,
  is_amc_lender: PropTypes.bool,
  panelistCounts: PropTypes.object, // Object mapping branch_id to panelist count
}

export default BranchHierarchyTree
